import AppStore from "@/store/AppStore"
import { ModuleName } from "@/store/AppStore.types"
import { debounce } from "@/utils/CommonFunctions"
import { Box, BoxProps, CircularProgress, SxProps } from "@mui/material"
import { DataGrid, DataGridProps, GridCellParams, GridColumnVisibilityModel, GridFilterModel, GridPaginationModel, GridSortModel, MuiEvent } from "@mui/x-data-grid"
import { GridApiCommunity } from "@mui/x-data-grid/internals"
import dayjs from "dayjs"
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { UseInfiniteQueryResult, useQueryClient, UseQueryResult } from "react-query"
import { filterInt, sortInt } from "../../../interface/InfiniteTable"
import { CustomColors } from "../../../theme/theme"
import { calculateToFromFilter } from "./InfiniteTableExt"

var customParseFormat = require("dayjs/plugin/customParseFormat")
var utc = require("dayjs/plugin/utc")
dayjs.extend(customParseFormat)
dayjs.extend(utc)

interface IDataGridProps {
  reactQuery: UseInfiniteQueryResult
  rowKey?: string
  infiniteTableKey: ModuleName
  refs: {
    filterRef: MutableRefObject<filterInt | {}>
    sortRef: MutableRefObject<sortInt | {}>
    isNextPage: MutableRefObject<boolean>
    tableAPIRef: MutableRefObject<GridApiCommunity>
  }
  mapper: {
    filterIdMap?: filterInt // @deprecated in support of Advanced Filter "filterKey" key in "columns" prop
    sortIdMap: filterInt // @to_be_deprecated in support of "sortKey" key in "columns" prop
  }
  rows: any
  columns: any
  onRowClick?: (params: any) => void
  onCellClick?: (params: GridCellParams, event: MuiEvent<React.MouseEvent>) => any
  slots?: {
    tableContainer?: BoxProps
  }
  totalCount?: number
  hiddenColumns?: GridColumnVisibilityModel
  getRowClassName?: DataGridProps["getRowClassName"]
  getCellClassName?: DataGridProps["getCellClassName"]
}

const InfiniteTable = ({
  rows,
  columns,
  onRowClick,
  onCellClick,
  mapper,
  refs,
  reactQuery,
  rowKey,
  infiniteTableKey,
  slots,
  hiddenColumns,
  totalCount,
  getRowClassName,
  getCellClassName,
}: IDataGridProps) => {
  const gridRef = useRef<HTMLDivElement>(null)
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const queryClient = useQueryClient()
  const { filterIdMap = {}, sortIdMap = {} } = mapper
  const { filterRef, sortRef, isNextPage, tableAPIRef } = refs
  const { isFetching, isFetchingNextPage, fetchNextPage } = reactQuery
  const MIN_HEIGHT = "392px"

  const CustomNoRowsOverlay = () => {
    return <>{isFetching ? <CircularProgress /> : rows.length === 0 ? <>{"No Records"}</> : ""}</>
  }

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    if (filterModel.items.length > 0) {
      const curr = filterModel.items[0]
      const key = filterIdMap[curr.field]
      const pair: filterInt = {
        [key]: curr.value,
      }

      if (curr.field && (curr.value || typeof curr.value === "boolean")) {
        const isDateValid = dayjs(pair[key], "YYYY-MM-DD", true).isValid()
        if (isDateValid) {
          pair[key] = dayjs.utc(pair[key]).unix() * 1000
        }
        const rangeFilter = calculateToFromFilter(AppStore.counterCardFilter[infiniteTableKey])
        filterRef.current = {
          ...pair,
          ...rangeFilter,
        }
      } else if (curr.value === "" || (curr.hasOwnProperty("value") && curr.value === undefined)) {
        filterRef.current = {}
      }

      if (curr.value || typeof curr.value === "boolean" || curr.value === "" || (curr.hasOwnProperty("value") && curr.value === undefined)) {
        queryClient.setQueryData(infiniteTableKey, (data: any) => ({
          pages: [],
        }))
        setPaginationModel(prev => {
          return { ...prev, page: 0 }
        })
        isNextPage.current = true
        if (!isFetchingNextPage) {
          fetchNextPage()
        }
      }
    }
  }, [])

  const onSortChange = (sortModel: GridSortModel) => {
    queryClient.setQueryData(infiniteTableKey, (data: any) => ({
      pages: [],
    }))
    setPaginationModel(prev => {
      return { ...prev, page: 0 }
    })
    isNextPage.current = true

    if (sortModel.length > 0) {
      const curr = sortModel?.[0]
      const pair: sortInt = {
        field_name: sortIdMap[curr.field] as string,
        orderBy: curr.sort === "asc" ? "asc" : "desc",
      }
      sortRef.current = pair
    } else {
      sortRef.current = {}
    }
    if (!isFetching) {
      fetchNextPage()
    }
  }

  const memoRow = useMemo(() => {
    return rows
  }, [rows])

  const handlePaginationChange = useCallback((pagination: GridPaginationModel) => {
    if (!isFetchingNextPage) {
      fetchNextPage({ pageParam: pagination.page + 1, })
      setPaginationModel(pagination)
    }
  }, [fetchNextPage, isFetchingNextPage])

  return (
    <Box
      className="InfiTable"
      {...slots?.tableContainer}
      sx={[{ height: "max(calc(100% - 80px), 400px)" }, { ...slots?.tableContainer?.sx }] as SxProps}
    >
      <DataGrid
        ref={gridRef}
        apiRef={tableAPIRef}
        rows={rows}
        getRowId={(row) => row[rowKey ?? "id"]}
        columns={columns}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={[]}
        rowCount={totalCount || 0} // Replace with actual row count from your API
        sx={{
          "&": {
            borderWidth: 0,
          },
          "& .MuiDataGrid-overlayWrapperInner": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
          "& .MuiDataGrid-virtualScroller": {
            height: "100%",
            minHeight: MIN_HEIGHT,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: CustomColors.adminAppBgColorV2,
            color: "#fff",
          },
          ".MuiDataGrid-sortIcon": {
            color: "white",
          },
          ".MuiSvgIcon-root": {
            color: "white",
          },
          "& MuiPopper-root, .MuiDataGrid-menuList": {
            color: CustomColors.adminAppTextPrimary,
            backgroundColor: "red",
          },
          ".MuiDataGrid-withBorderColor": {
            borderColor: CustomColors.adminAppOutlineColor,
          },
          "& .MuiMenuItem-root:hover": {
            backgroundColor: "#272D24",
            filter: " invert(20%)",
          },
          ".MuiDataGrid-columnSeparator .MuiSvgIcon-root": {
            color: CustomColors.adminAppOutlineColor,
          },
          borderColor: "transparent",
          width: "auto",
          color: CustomColors.adminAppTextSecondary,
        }}
        
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        filterMode="server"
        sortingMode="server"
        onFilterModelChange={onFilterChange}
        onSortModelChange={onSortChange}
        filterDebounceMs={1000}
        columnVisibilityModel={{ ...hiddenColumns }}
        slotProps={{
          filterPanel: {
            disableAddFilterButton: false,
            sx: {
              "& .MuiDataGrid-filterFormOperatorInput": {
                display: "none",
              },
            },
          },
        }}
        loading={isFetching || isFetchingNextPage}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        getRowClassName={getRowClassName}
        getCellClassName={getCellClassName}
      />
    </Box>
  )
}

export default InfiniteTable